import React, { CSSProperties, useMemo } from 'react';
import {
  Dropdown,
  Field,
  Option,
  FluentProvider,
  webLightTheme,
  DropdownProps,
  InfoLabel,
  LabelProps,
} from '@fluentui/react-components';
import IDropdownOption from '../../utils/types/IDropdownOption';

interface IMultiSelectDropdownProps {
  label?: string;
  className?: string;
  placeholder?: string;
  options: IDropdownOption[];
  selectedKeys?: string[];
  required?: boolean;
  disabled?: boolean;
  onChange?: (event: React.FormEvent<HTMLElement>, data: IDropdownOption[]) => void;
  errorMessage?: string;
  tooltipMessage?: string;
  styles?: CSSProperties;
  'aria-label'?: string;
}

const MultiSelectDropdown = (props: IMultiSelectDropdownProps): JSX.Element => {
  const {
    label,
    className,
    placeholder,
    options = [],
    selectedKeys = [],
    required,
    disabled,
    onChange,
    errorMessage,
    tooltipMessage,
    styles,
    'aria-label': ariaLabel,
  } = props;

  // Calculate display value from selected keys
  const displayValue = useMemo(() => {
    if (!selectedKeys || selectedKeys.length === 0) return '';
    return selectedKeys
      .map((key) => {
        const option = options.find((opt) => opt.key.toString() === key);
        return option?.text;
      })
      .filter(Boolean)
      .join(', ');
  }, [selectedKeys, options]);

  const handleChange: DropdownProps['onOptionSelect'] = (event, data) => {
    if (onChange) {
      const selected = data.selectedOptions;
      const selectedDropdownOptions = options.filter((option) =>
        selected.includes(option.key.toString()),
      );
      onChange(event, selectedDropdownOptions);
    }
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <Field
        label={
          tooltipMessage
            ? {
                children: (_: unknown, slotProps: LabelProps) => (
                  <InfoLabel {...slotProps} info={tooltipMessage}>
                    {label}
                  </InfoLabel>
                ),
              }
            : label
        }
        required={required}
        validationMessage={errorMessage}
      >
        <Dropdown
          className={className}
          placeholder={placeholder}
          multiselect
          disabled={disabled}
          value={displayValue}
          selectedOptions={selectedKeys}
          onOptionSelect={handleChange}
          style={styles}
          aria-label={ariaLabel || label}
        >
          {options?.map((option) => (
            <Option key={option.key.toString()} value={option.key.toString()}>
              {option.text}
            </Option>
          ))}
        </Dropdown>
      </Field>
    </FluentProvider>
  );
};
export default MultiSelectDropdown;
